import { useMemo } from 'react';

import { useLocations } from '../hooks';
import type { InventoryLocationSummary } from '../model/types';

export interface LocationLookup {
  locations: InventoryLocationSummary[];
  isLoading: boolean;
  isError: boolean;
  /** Resolved location name, or the raw id itself when it isn't (yet) resolvable. */
  getLocationName: (locationId: string) => string;
}

/**
 * Fetches the tenant's fulfilment locations once (cached — see `useLocations`) and exposes an
 * id→name lookup for display. Every inventory screen that shows a `locationId` uses this instead
 * of the raw id, falling back to the id itself if the location can't be resolved (e.g. it was
 * deleted, or the tenant has more locations than the single lookup page covers).
 */
export function useLocationLookup(): LocationLookup {
  const query = useLocations();
  const byId = useMemo(() => {
    const map = new Map<string, InventoryLocationSummary>();
    for (const location of query.data ?? []) {
      map.set(location.id, location);
    }
    return map;
  }, [query.data]);

  return {
    locations: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    getLocationName: (locationId: string) => byId.get(locationId)?.name ?? locationId,
  };
}
